/**
 * A campaign's brief: what creators' content must and must not contain. Request pages and public
 * campaign pages show it the same way: the description, then the exact elements content must
 * include, then the requirements read by people and models, and the brand kit's images. Empty
 * lists are left out.
 */

import { brandAssetFile } from "@morcast/campaigns";
import { Download } from "lucide-react";
import { useId } from "react";

import { styles } from "../components/ui.js";
import type { BrandKitAsset, CampaignRequest } from "./requests.js";

/** A brand kit image's public address, once a manifest names it. */
export function publicAssetUrl(asset: BrandKitAsset): string {
  return `/documents/assets/${brandAssetFile(asset)}`;
}

/** A brand kit image's address for signed-in wallets, before the request is accepted. */
export function privateAssetUrl(asset: BrandKitAsset): string {
  return `/api/brand-assets/${asset.sha256}`;
}

export function BriefCard({
  brief,
  assetUrl = publicAssetUrl,
}: {
  brief: CampaignRequest["brief"];
  /** Where the brand kit's images are read from: public for campaigns, private for requests. */
  assetUrl?: (asset: BrandKitAsset) => string;
}) {
  const titleId = useId();
  const elements = [
    { label: "Disclosure", items: [brief.disclosure] },
    { label: "Hashtags", items: brief.requiredHashtags },
    { label: "Mentions", items: brief.requiredMentions },
    { label: "Links", items: brief.requiredLinks },
    { label: "Required phrases", items: brief.requiredText },
  ].filter((element) => element.items.length > 0);

  return (
    <section aria-labelledby={titleId} className={`mt-6 text-sm ${styles.card}`}>
      <h2 id={titleId} className="text-lg font-semibold tracking-tight">
        Brief
      </h2>
      <p className="mt-3 whitespace-pre-line text-zinc-700 dark:text-zinc-300">
        {brief.description}
      </p>

      <h3 className="mt-6 font-medium">Content must include</h3>
      <dl className="mt-2 space-y-2">
        {elements.map((element) => (
          <div key={element.label} className="flex flex-col gap-1 sm:flex-row sm:gap-3">
            <dt className="shrink-0 text-zinc-500 sm:w-36 sm:pt-0.5 dark:text-zinc-400">
              {element.label}
            </dt>
            <dd className="flex min-w-0 flex-wrap gap-1.5">
              {element.items.map((item) => (
                <span
                  key={item}
                  className="rounded-md bg-zinc-100 px-2 py-0.5 font-mono text-xs break-all text-zinc-800 dark:bg-ink-800 dark:text-zinc-200"
                >
                  {item}
                </span>
              ))}
            </dd>
          </div>
        ))}
      </dl>

      <BriefList title="Content requirements" items={brief.semanticRequirements} />
      <BriefList title="Visual requirements" items={brief.visualRequirements} />
      <BriefList title="Prohibited content" items={brief.prohibitedContent} />
      {brief.allowedEdits && <BriefList title="Allowed edits" items={[brief.allowedEdits]} />}
      {brief.assets && brief.assets.length > 0 && (
        <div className="mt-5">
          <h3 className="font-medium">Brand kit</h3>
          <p className="mt-1 text-zinc-600 dark:text-zinc-400">
            Use these in your content. Visual requirements that name one are checked against it.
          </p>
          <ul className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {brief.assets.map((asset) => (
              <li key={asset.sha256}>
                <a
                  href={assetUrl(asset)}
                  download={`${asset.label}.${brandAssetFile(asset).split(".").at(-1)}`}
                  className="group block rounded-xl bg-zinc-50 p-2 ring-1 ring-zinc-200 transition hover:ring-brand-500/50 dark:bg-white/[0.04] dark:ring-white/10"
                >
                  {/*
                    A light tile on both themes: brand kit images, such as a dark logo on a transparent
                    ground, are made to be seen on white.
                  */}
                  <img
                    src={assetUrl(asset)}
                    alt={asset.label}
                    loading="lazy"
                    className="aspect-square w-full rounded-lg bg-white object-contain dark:bg-zinc-100"
                  />
                  <span className="mt-2 flex items-center justify-between gap-2 px-1 text-xs font-medium">
                    <span className="truncate">{asset.label}</span>
                    <Download
                      aria-hidden
                      className="size-3.5 shrink-0 text-zinc-400 group-hover:text-brand-600 dark:text-zinc-500 dark:group-hover:text-brand-400"
                    />
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function BriefList({ title, items }: { title: string; items: string[] }) {
  if (items.length === 0) {
    return null;
  }
  return (
    <div className="mt-5">
      <h3 className="font-medium">{title}</h3>
      <ul className="mt-1.5 list-disc space-y-1 pl-5 text-zinc-700 dark:text-zinc-300">
        {items.map((item) => (
          <li key={item} className="break-words">
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}
